import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { GetAggregatedValueDto, IBindable, Identifier, IValueViewModel, AggregationMode, Group, Dictionary, TreeType, ActualDataType } from '../../models';
import { ValueAcquisitionDto } from '../../models/DTO';
import {
    IGroupService,
    IIdentifierVmService,
    IRequestService,
    IDateRangeService,
    IwidgetIntervalHelperService,
    INameBuilderService,
    IFormatNumberHelper
} from '..';
import { groupIdentifierSeparator, guidEmpty, getUnitName, toDictionary } from '../../common';
import {
    TableByGroupWidgetSeries,
    TableByGroupWidgetConfiguration,
    ITableByGroupVm,
    IRowViewModel,
    IdentifierRow,
    ColumnViewModel
} from '../../widgets/tableByGroup';
import { formatValueForCsv } from '../../services/data/formatValueForCsv';

export interface HierarchicalGroupTuple {
    group: Group;
    absolutePath: string;
    relativePath: string;
}

export interface ITableByGroupAggregation {
    actualValue?: number;
    color: string;
    decimals: number;
    name: string;
    value: string;
}

export interface ITableByGroupService {
    getGroupViewModels(
        configuration: TableByGroupWidgetConfiguration,
        selectedGroupGuid: string,
        identifiers: IdentifierRow[]
    ): angular.IPromise<IRowViewModel[]>;
    getReadConfiguration(
        configuration: TableByGroupWidgetConfiguration,
        selectedGroupGuid: string,
        values?: IRowViewModel[]
    ): angular.IPromise<GetAggregatedValueDto>;
    getSeriesList(configuration: TableByGroupWidgetConfiguration, selectedGroupGuid: string, values?: IRowViewModel[]): angular.IPromise<ValueAcquisitionDto[]>;
    buildTable(
        configuration: TableByGroupWidgetConfiguration,
        values: IRowViewModel[],
        aggregatedList: ITableByGroupAggregation[],
        identifiers: IdentifierRow[]
    ): string[][];
    getValueName(valueType: string): string;
    aggregateValues(values: IRowViewModel[], identifiers: IdentifierRow[]): ITableByGroupAggregation[];
    getIdentifierRows(series: TableByGroupWidgetSeries[], viewModels: Dictionary<IValueViewModel>): ITableByGroupVm[];
    mapGroupsAndIdentifiersAsync(
        groups: TreeType[],
        identifiers: IdentifierRow[],
        allGroups: TreeType[],
        isSearchByRelativePath: boolean
    ): angular.IPromise<IRowViewModel[]>;
    getGroups(configuration: TableByGroupWidgetConfiguration, groups: Group[], selectedGroupGuid: string): TreeType[];
    getFirstAncestorWithUniqueName(guid: string): angular.IPromise<HierarchicalGroupTuple>;
}

export class TableByGroupService implements ITableByGroupService {
    static serviceId = 'tableByGroupService';

    static $inject = [
        '$q',
        'formatNumberHelper',
        'groupService',
        'identifierVmService',
        'requestService',
        'dateRangeService',
        'widgetIntervalHelperService',
        'nameBuilderService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _groupService: IGroupService,
        private _identifierVmService: IIdentifierVmService,
        private _requestService: IRequestService,
        private _dateRangeService: IDateRangeService,
        private _intervaleHelper: IwidgetIntervalHelperService,
        private _nameBuilderService: INameBuilderService
    ) {}

    getGroupViewModels(
        configuration: TableByGroupWidgetConfiguration,
        selectedGroupGuid: string,
        identifiers: IdentifierRow[]
    ): angular.IPromise<IRowViewModel[]> {
        const types = configuration.groupTypes;
        const guids = configuration.groupGuidCollection;

        if ((types && types.length > 0) || (guids && guids.length) || (configuration.isBoundToGroup && selectedGroupGuid)) {
            return this._groupService
                .getAllAsTree()
                .then((data) => {
                    const allowedGroups = configuration.isBoundToGroup ? this.getBoundGroupAndChildrenIfRequired(data, configuration, selectedGroupGuid) : [];
                    return { all: data, filtered: this.filterGroups(data, types, guids, allowedGroups) };
                })
                .then((data) => this.mapGroupsAndIdentifiersAsync(data.filtered, identifiers, data.all, configuration.isSearchByRelativePath));
        } else {
            return this._$q.when([]);
        }
    }

    getGroups(configuration: TableByGroupWidgetConfiguration, groups: Group[], selectedGroupGuid: string) {
        const data = this._groupService.convertToTree(groups);
        const types = configuration.isBoundToGroup ? [] : configuration.groupTypes;
        const allowedGroups = configuration.isBoundToGroup ? this.getBoundGroupAndChildrenIfRequired(data, configuration, selectedGroupGuid) : [];
        const groupGuids = configuration.groupGuidCollection;

        return this.filterGroups(data, types, groupGuids, allowedGroups);
    }

    private getBoundGroupAndChildrenIfRequired(data: TreeType[], configuration: TableByGroupWidgetConfiguration, selectedGroupGuid: string): string[] {
        let result: string[] = [];

        if (configuration.isBoundToGroup && selectedGroupGuid) {
            const selected = _.find(data, (g) => g.guid == selectedGroupGuid);
            if (selected) {
                const getDescendants = ({ guid, children }): string[] => (children ? [guid, ...children.flatMap(getDescendants)] : guid);
                const children = getDescendants(selected);
                result = result.concat(selected.guid, children);
            }
        }

        return result;
    }

    /**
     * Filter group according to parameters
     * @param data
     * @param types the list of group types selected
     * @param groupGuids the list of groups selected
     * @param allowedGroups if empty all group are allowed, otherwise restrict to them
     */
    private filterGroups(data: TreeType[], types: string[], groupGuids: string[], allowedGroups: string[]): TreeType[] {
        return data
            .filter((d) => (types ?? []).indexOf(d.type) >= 0 || (groupGuids ?? []).indexOf(d.guid) >= 0)
            .filter((d) => (allowedGroups?.length > 0 ? allowedGroups.indexOf(d.guid) >= 0 : true));
    }

    getIdentifierRows(series: TableByGroupWidgetSeries[], viewModels: Dictionary<IValueViewModel>): ITableByGroupVm[] {
        const mappedSeries = this.mapSeriesFromViewModels(series, viewModels);
        const identifiers = mappedSeries.sort((d) => (d as any).order);
        return identifiers;
    }

    mapGroupsAndIdentifiersAsync(
        groups: TreeType[],
        identifiers: IdentifierRow[],
        allGroups: TreeType[],
        isSearchByRelativePath: boolean
    ): angular.IPromise<IRowViewModel[]> {
        const groupGuids = _.uniq(identifiers.map((i) => i?.viewModel?.groupList?.[0].guid).filter((d) => !!d));
        const promisesDic: Dictionary<angular.IPromise<HierarchicalGroupTuple>> = {};
        groupGuids.forEach((gg) => (promisesDic[gg] = this.getFirstAncestorWithUniqueName(gg)));
        return this._$q.all(promisesDic).then((data) => {
            const values = groups
                // remap from all group to be sure to use a TreeType
                // it seems that groups may only contains Group type
                .map((d) => allGroups.find((g) => g.guid === d.guid))
                .map((found) => this.mapGroupAndIdentifiers(found, identifiers, data, isSearchByRelativePath));
            return values;
        });
    }

    private mapGroupAndIdentifiers(
        group: TreeType,
        series: IdentifierRow[],
        hierarchicals: Dictionary<HierarchicalGroupTuple>,
        isSearchByRelativePath: boolean
    ): IRowViewModel {
        const values = series.map((row) => {
            const hierarchical = hierarchicals[row.viewModel.groupList[0].guid];
            return isSearchByRelativePath ? this.mapSerieRelativeToGroup(group, row, hierarchical) : this.mapSerieInGroup(group, row);
        });

        return {
            name: group.name,
            guid: group.guid,
            original: group,
            values
        };
    }

    /** Simple mapping of series in selected group**/
    private mapSerieInGroup(group: TreeType, row: IdentifierRow): ColumnViewModel {
        return this.getColumnViewModelFromIdentifiers(row, group.identifierCollection, './');
    }

    /** Complex mapping of series in target group using a relative path search **/
    private mapSerieRelativeToGroup(group: TreeType, row: IdentifierRow, hierarchical: HierarchicalGroupTuple): ColumnViewModel {
        let identifierCollection = group.identifierCollection;
        let filteredGroups = [group];

        const relativePath = hierarchical.relativePath;
        if (relativePath) {
            relativePath
                .split('/')
                .filter((s) => s !== '.')
                .forEach((s) => {
                    try {
                        filteredGroups = _.chain(filteredGroups)
                            .map((dd) => dd.children ?? [])
                            .flatten()
                            .filter((dd) => dd && dd.name === s)
                            .value() as TreeType[];
                    } catch (e) {
                        console.error(e);
                    }
                });
            identifierCollection = _.chain(filteredGroups)
                .map((dd) => dd.identifierCollection)
                .flatten()
                .value();
        }

        return this.getColumnViewModelFromIdentifiers(row, identifierCollection, relativePath);
    }

    /** Return a ColumnViewModel from a series (row) by searching name in a list of possible identifier **/
    private getColumnViewModelFromIdentifiers(row: IdentifierRow, identifiers: Identifier[], relativePath: string): ColumnViewModel {
        const updated = _.find(identifiers, (identifier: Identifier) => identifier.name === row.name);
        return {
            color: row.color,
            decimals: row.decimals,
            format: row.format,
            guid: updated?.guid ?? guidEmpty,
            name: row.name,
            relativePath: relativePath,
            valueType: row.value
        };
    }

    private mapSeriesFromViewModels(series: TableByGroupWidgetSeries[], viewModels: Dictionary<IValueViewModel>): ITableByGroupVm[] {
        return series.map((found) => {
            const d = viewModels[found.guid];
            const unitName = getUnitName(d.unitName, found.value);
            const legend = found.legend;

            return {
                aggregationMode: found.aggregationMode,
                color: found.color,
                decimals: found.decimals,
                format: found.format,
                guid: d.guid,
                isUnitVisible: found.isUnitVisible,
                legend: legend,
                longComment: d.longComment,
                name: d.name,
                relativePath: found.relativePath,
                shortComment: d.shortComment,
                unit: unitName,
                value: found.value,
                viewModel: d
            };
        });
    }

    getReadConfiguration(
        widgetConfig: TableByGroupWidgetConfiguration,
        selectedGroupGuid: string,
        values?: IRowViewModel[]
    ): angular.IPromise<GetAggregatedValueDto> {
        return this.getSeriesList(widgetConfig, selectedGroupGuid, values).then((guidList) => {
            const interval = this._intervaleHelper.getInterval(
                this._dateRangeService.beginDate() as string,
                this._dateRangeService.endDate() as string,
                widgetConfig
            );
            const beginDate = interval.beginMoment.toISOString();
            const endDate = interval.endMoment.toISOString();
            const config: GetAggregatedValueDto = {
                beginDateTime: beginDate,
                endDateTime: endDate,
                aggregationMode: widgetConfig.aggregationMode,
                hasQuality: widgetConfig.hasQuality,
                hasAlarm: widgetConfig.hasAlarm,
                list: guidList
            };
            return config;
        });
    }

    getSeriesList(configuration: TableByGroupWidgetConfiguration, selectedGroupGuid: string, values?: IRowViewModel[]): angular.IPromise<IBindable[]> {
        const defered = this._$q.defer<IBindable[]>();
        let promise: angular.IPromise<IRowViewModel[]>;

        if (!values) {
            promise = this._identifierVmService
                .getAllViewModels()
                .then((vms) => this.mapSeriesFromViewModels(configuration.series, toDictionary(vms)))
                .then((vms) => this.getGroupViewModels(configuration, selectedGroupGuid, vms));
        } else {
            promise = this._$q.when(values);
        }

        promise
            .then((data) => {
                const series = _.chain(data)
                    .map((d) => d.values)
                    .flatten();
                const guidList: IBindable[] = series
                    .map((d: ColumnViewModel) => {
                        return {
                            guid: d.guid,
                            value: d.valueType,
                            originalGuid: (d as any).originalGuid,
                            isBoundToGroup: d.isBoundToGroup,
                            relativePath: d.relativePath
                        };
                    })
                    .filter((d) => d.guid !== guidEmpty)
                    .sortBy((d) => d.guid)
                    .value();
                defered.resolve(guidList);
            })
            .catch(() => defered.reject());

        return defered.promise;
    }

    buildTable(
        configuration: TableByGroupWidgetConfiguration,
        values: IRowViewModel[],
        aggregatedList: ITableByGroupAggregation[],
        identifiers: IdentifierRow[]
    ): string[][] {
        const headings: string[][] = [];
        if (configuration.hasName) {
            headings.push(this.getHeadings('Name', (i) => i.name, configuration, identifiers));
        }

        if (configuration.hasShortComment) {
            headings.push(this.getHeadings('Short comment', (i) => i.shortComment, configuration, identifiers));
        }
        if (configuration.hasValueType) {
            headings.push(this.getHeadings('Value type', (i) => this.getValueName(i.value), configuration, identifiers));
        }
        if (configuration.hasUnits !== false) {
            headings.push(this.getHeadings('Unit', (i) => i.unit, configuration, identifiers));
        }

        const formatter = this._formatNumberHelper.getFormatter(null, false);
        const body: string[][] = values.map((v) => {
            const row = [];
            if (!configuration.hideFieldDescriptions) {
                row.push(v.name);
            }

            if (configuration.showGroupShortComment) {
                row.push(v.original.shortComment);
            }
            if (configuration.showGroupLongComment) {
                row.push(v.original.longComment);
            }

            v.values.forEach((i) => {
                const formattedValue =
                    i.dataType === ActualDataType.Float ? (i.actualValue !== 'NaN' ? formatValueForCsv(i.actualValue, formatter) : 'NaN') : i.value;
                row.push(formattedValue);
                if (configuration.hasQuality) {
                    //TODO
                    row.push(' ');
                }
                if (configuration.hasAlarm) {
                    //TODO
                    row.push(' ');
                }
            });
            return row;
        });

        let footer: string[][];
        if (!!configuration.hasAggregationRow) {
            const row = [];
            if (!configuration.hideFieldDescriptions) {
                row.push('Aggregated value');
            }
            if (configuration.showGroupShortComment) {
                row.push(' ');
            }
            if (configuration.showGroupLongComment) {
                row.push(' ');
            }
            aggregatedList.forEach((a) => row.push(a.actualValue));
            footer = [row];
        } else {
            footer = [];
        }

        const dataArray = headings.concat(body).concat(footer);

        return dataArray;
    }

    private getHeadings(
        name: string,
        callback: (i: IdentifierRow) => string,
        configuration: TableByGroupWidgetConfiguration,
        identifiers: IdentifierRow[]
    ): string[] {
        const names = [];

        if (!configuration.hideFieldDescriptions) {
            names.push(name);
        }

        if (configuration.showGroupShortComment) {
            names.push(' ');
        }
        if (configuration.showGroupLongComment) {
            names.push(' ');
        }

        identifiers.forEach((i) => {
            const items = callback(i);
            names.push(items);
            if (configuration.hasQuality) {
                names.push(' ');
            }
            if (configuration.hasAlarm) {
                names.push(' ');
            }
        });
        return names;
    }

    getValueName(valueType: string): string {
        return this._nameBuilderService.getValueName(valueType);
    }

    aggregateValues(values: IRowViewModel[], identifiers: IdentifierRow[]): ITableByGroupAggregation[] {
        const list = _.chain(values)
            .map((d) => d.values)
            .flatten()
            .groupBy((d) => d.name + d.valueType)
            .value();
        const aggregatedList = identifiers.map((d) => {
            const res = {
                actualValue: null,
                color: d.color,
                decimals: d.decimals,
                name: d.name,
                value: '-'
            };
            const key = d.name + d.value;
            if (list[key]) {
                const filtered = list[key].filter((dd) => dd.actualValue !== null && dd.actualValue !== undefined && dd.actualValue !== 'NaN');
                if (filtered.length > 0) {
                    let formatted = '-';
                    let aggregationFunction: (data: any[], selector: (d: any) => number) => number;
                    switch (d.aggregationMode) {
                        case AggregationMode.Sum:
                            aggregationFunction = d3.sum;
                            break;
                        case AggregationMode.Average:
                            aggregationFunction = d3.mean;
                            break;
                        default:
                            break;
                    }
                    if (aggregationFunction) {
                        const value = aggregationFunction(filtered, (dd) => dd.actualValue);
                        res.actualValue = value;
                        const format = d3.format(`2.${d.decimals}f`);
                        formatted = format(value);
                    }
                    res.value = formatted;
                }
            }
            return res;
        });
        return aggregatedList;
    }

    getFirstAncestorWithUniqueName(guid: string): angular.IPromise<HierarchicalGroupTuple> {
        return this._requestService.getAtV1(`group/GetFirstAncestorWithUniqueName/${guid}`);
    }
}
